import { useMemo } from 'react';
import { useTodos, useTodoLists } from '../useTodos';
import { useNotifications } from '../useNotifications';
import { useNotes } from '../useNotes';
import { useUser } from '../../contexts/UserContext';
import { TodoItem, TodoList } from '../../types/todo';
import { Notification } from '../../types/notifications';
import { Note } from '../../types/notes';

export interface DashboardData {
  // Raw data
  todos: TodoItem[];
  todoLists: TodoList[];
  notes: Note[];
  notifications: Notification[];
  
  // Basic aggregations
  totalTasks: number;
  completedTasks: number;
  pendingTasks: number;
  overdueTasks: number;
  todaysDueTasks: number;
  highPriorityTasks: number;
  unreadNotifications: number;
  
  // Completion metrics
  overallCompletionRate: number;
  todaysCompletionRate: number;
  
  // List insights
  activeLists: TodoList[];
  mostActiveLists: Array<{ list: TodoList; taskCount: number; completionRate: number }>;
  
  // Priority breakdown
  priorityBreakdown: {
    high: { total: number; completed: number; completionRate: number };
    medium: { total: number; completed: number; completionRate: number };
    low: { total: number; completed: number; completionRate: number };
  };
  
  // Recent activity
  recentlyCompletedTasks: TodoItem[];
  recentlyCreatedTasks: TodoItem[];
  
  // Time-based insights
  tasksCreatedThisWeek: number;
  tasksCompletedThisWeek: number;
  
  // Health scores
  productivityScore: number; // 0-100
  listHealthScores: Array<{ listId: number; listName: string; health: number }>;
  // Loading flags (optional extension)
  isLoading?: boolean;
  loadingFlags?: {
    todos: boolean;
    lists: boolean;
    notes: boolean;
    notifications: boolean;
  };
}

/**
 * Main dashboard data hook that aggregates data from all sources
 * and provides computed insights for the dashboard components
 */
export const useDashboardData = (): DashboardData => {
  const { currentUser } = useUser();
  const { data: todos = [], isLoading: todosLoading } = useTodos();
  const { data: todoLists = [], isLoading: listsLoading } = useTodoLists();
  const { data: notes = [], isLoading: notesLoading } = useNotes(currentUser?.email || '');
  const { notifications, isLoading: notificationsLoading } = useNotifications(currentUser?.email || '');

  return useMemo(() => {
    // Basic metrics
    const totalTasks = todos.length;
    const completedTasks = todos.filter(t => t.isCompleted).length;
    const pendingTasks = totalTasks - completedTasks;

    // Date helpers
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

    // Overdue tasks (not completed and past due date)
    const overdueTasks = todos.filter(t => {
      if (!t.dueDate || t.isCompleted) return false;
      return new Date(t.dueDate) < today;
    }).length;

    // Today's due tasks
    const todaysDueTasks = todos.filter(t => {
      if (!t.dueDate) return false;
      const dueDate = new Date(t.dueDate);
      return dueDate.toDateString() === today.toDateString();
    }).length;

    // High priority pending tasks
    const highPriorityTasks = todos.filter(t => 
      t.priority === 'High' && !t.isCompleted
    ).length;

    // Completion rates
    const overallCompletionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
    
    // Today's tasks (created today or due today)
    const todaysTasks = todos.filter(t => {
      const createdToday = new Date(t.createdAt).toDateString() === today.toDateString();
      const dueToday = t.dueDate && new Date(t.dueDate).toDateString() === today.toDateString();
      return createdToday || dueToday;
    });
    const todaysCompletedTasks = todaysTasks.filter(t => t.isCompleted).length;
    const todaysCompletionRate = todaysTasks.length > 0 ? 
      Math.round((todaysCompletedTasks / todaysTasks.length) * 100) : 0;

    // Priority breakdown
    const priorityBreakdown = {
      high: {
        total: todos.filter(t => t.priority === 'High').length,
        completed: todos.filter(t => t.priority === 'High' && t.isCompleted).length,
        completionRate: 0
      },
      medium: {
        total: todos.filter(t => t.priority === 'Medium').length,
        completed: todos.filter(t => t.priority === 'Medium' && t.isCompleted).length,
        completionRate: 0
      },
      low: {
        total: todos.filter(t => t.priority === 'Low').length,
        completed: todos.filter(t => t.priority === 'Low' && t.isCompleted).length,
        completionRate: 0
      }
    };

    // Calculate completion rates for priorities
    priorityBreakdown.high.completionRate = priorityBreakdown.high.total > 0 ? 
      Math.round((priorityBreakdown.high.completed / priorityBreakdown.high.total) * 100) : 0;
    priorityBreakdown.medium.completionRate = priorityBreakdown.medium.total > 0 ? 
      Math.round((priorityBreakdown.medium.completed / priorityBreakdown.medium.total) * 100) : 0;
    priorityBreakdown.low.completionRate = priorityBreakdown.low.total > 0 ? 
      Math.round((priorityBreakdown.low.completed / priorityBreakdown.low.total) * 100) : 0;

    // List insights
    const activeLists = todoLists.filter(list => list.items && list.items.length > 0);
    const mostActiveLists = todoLists
      .map(list => ({
        list,
        taskCount: list.items ? list.items.length : 0,
        completionRate: list.totalCount > 0 ? Math.round((list.completedCount / list.totalCount) * 100) : 0
      }))
      .filter(item => item.taskCount > 0)
      .sort((a, b) => b.taskCount - a.taskCount)
      .slice(0, 5);

    // Recent activity
    const recentlyCompletedTasks = todos
      .filter(t => t.isCompleted && t.completedAt)
      .sort((a, b) => new Date(b.completedAt!).getTime() - new Date(a.completedAt!).getTime())
      .slice(0, 10);

    const recentlyCreatedTasks = todos
      .filter(t => !t.isCompleted)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 5);

    // Time-based insights
    const tasksCreatedThisWeek = todos.filter(t => 
      new Date(t.createdAt) >= weekAgo
    ).length;

    const tasksCompletedThisWeek = todos.filter(t => 
      t.isCompleted && t.completedAt && new Date(t.completedAt) >= weekAgo
    ).length;

    // Productivity score calculation (0-100)
    // Factors: completion rate (40%), deadline adherence (35%), consistency (25%)
    const deadlineAdherence = totalTasks > 0 ? 
      Math.max(0, 100 - ((overdueTasks / totalTasks) * 100)) : 100;
    
    // Simple consistency metric based on recent activity
    const consistencyScore = tasksCreatedThisWeek > 0 ? 
      Math.min(100, (tasksCompletedThisWeek / tasksCreatedThisWeek) * 100) : 0;
    
    const productivityScore = Math.round(
      (overallCompletionRate * 0.4) + 
      (deadlineAdherence * 0.35) + 
      (consistencyScore * 0.25)
    );

    // List health scores
    const listHealthScores = todoLists.map(list => ({
      listId: list.id,
      listName: list.name,
      health: list.totalCount > 0 ? Math.round((list.completedCount / list.totalCount) * 100) : 0
    }));

    // Unread notifications
  const unreadNotifications = notifications.filter((n: Notification) => !n.isRead).length;

  return {
      // Raw data
      todos,
      todoLists,
      notes,
      notifications,
      
      // Basic aggregations
      totalTasks,
      completedTasks,
      pendingTasks,
      overdueTasks,
      todaysDueTasks,
      highPriorityTasks,
      unreadNotifications,
      
      // Completion metrics
      overallCompletionRate,
      todaysCompletionRate,
      
      // List insights
      activeLists,
      mostActiveLists,
      
      // Priority breakdown
      priorityBreakdown,
      
      // Recent activity
      recentlyCompletedTasks,
      recentlyCreatedTasks,
      
      // Time-based insights
      tasksCreatedThisWeek,
      tasksCompletedThisWeek,
      
      // Health scores
      productivityScore,
      listHealthScores,
      // Loading
      isLoading: todosLoading || listsLoading || notesLoading || notificationsLoading,
      loadingFlags: {
        todos: todosLoading,
        lists: listsLoading,
        notes: notesLoading,
        notifications: notificationsLoading
      }
    };
  }, [todos, todoLists, notes, notifications, currentUser?.email, todosLoading, listsLoading, notesLoading, notificationsLoading]);
};

/**
 * Hook for loading state across all dashboard data sources
 */
export const useDashboardLoadingState = () => {
  const { currentUser } = useUser();
  const { isLoading: todosLoading } = useTodos();
  const { isLoading: listsLoading } = useTodoLists();
  const { isLoading: notesLoading } = useNotes(currentUser?.email || '');
  const { isLoading: notificationsLoading } = useNotifications(currentUser?.email || '');

  return {
    isLoading: todosLoading || listsLoading || notesLoading || notificationsLoading,
    todosLoading,
    listsLoading,
    notesLoading,
    notificationsLoading
  };
};